import {useSyncExternalStore} from 'react';

interface InternalData<T> {
  val: T | undefined;
  v: number;
}

export interface MapStoreApi<Key, Value> {
  getData: (key: Key) => Value | undefined;
  getAllData: () => Map<Key, Value>;
  setData: (key: Key, value: Value) => void;
  batchSetData: (batch: {key: Key; value: Value}[]) => void;
  useData: (key: Key | undefined) => Value | undefined;
  useAllData: () => Map<Key, Value>;
  unsetData: (key: Key) => void;
  batchUnsetData: (batch: Key[]) => void;
}

export function createMapStore<Key, Value>(): MapStoreApi<Key, Value> {
  const data = new Map<Key, InternalData<Value>>();
  let rawData: InternalData<Map<Key, Value>> = {val: new Map<Key, Value>(), v: 0};
  const listeners = new Map<Key, Set<() => void>>();
  const multiListeners = new Set<() => void>();

  function getData(key: Key): Value | undefined {
    return data.get(key)?.val;
  }

  function getAllData(): Map<Key, Value> {
    return rawData.val ?? new Map();
  }

  function setData(key: Key, value: Value): void {
    batchSetData([{key, value}]);
  }

  function batchSetData(batch: {key: Key; value: Value}[]): void {
    const newRawDataVal = new Map(rawData.val === undefined ? [] : [...rawData.val.entries()]);
    for (const {key, value} of batch) {
      const listenersForKey = listeners.get(key);
      const valueForKey = data.get(key);
      const v = (valueForKey?.v ?? -1) + 1;
      const newValueForKey = {val: value, v};

      data.set(key, newValueForKey);
      newRawDataVal.set(key, value);

      if (listenersForKey) {
        for (const cb of listenersForKey) {
          cb();
        }
      }
    }

    rawData = {val: newRawDataVal, v: rawData.v + 1};
    for (const cb of multiListeners) {
      cb();
    }
  }

  function unsetData(key: Key): void {
    batchUnsetData([key]);
  }

  function batchUnsetData(batch: Key[]): void {
    const newRawDataVal = new Map(rawData.val === undefined ? [] : [...rawData.val.entries()]);
    for (const key of batch) {
      const listenersForKey = listeners.get(key);
      const valueForKey = data.get(key);
      const v = (valueForKey?.v ?? -1) + 1;
      const newValueForKey = {val: undefined, v};

      data.set(key, newValueForKey);
      newRawDataVal.delete(key);

      if (listenersForKey) {
        for (const cb of listenersForKey) {
          cb();
        }
      }
    }

    rawData = {val: newRawDataVal, v: rawData.v + 1};
    for (const cb of multiListeners) {
      cb();
    }
  }

  function subscribeToKey(key: Key, callback: () => void): () => void {
    let listenersForKey = listeners.get(key);
    if (!listenersForKey) {
      listenersForKey = new Set();
      listeners.set(key, listenersForKey);
    }
    listenersForKey.add(callback);
    return () => {
      listenersForKey?.delete(callback);
      if (listenersForKey?.size === 0) {
        listeners.delete(key);
      }
    };
  }

  function subscribeToAll(callback: () => void): () => void {
    multiListeners.add(callback);
    return () => {
      multiListeners.delete(callback);
    };
  }

  function useData(key: Key | undefined): Value | undefined {
    const subscribe = (onStoreChange: () => void): (() => void) => {
      if (key === undefined) {
        return () => {};
      }
      return subscribeToKey(key, onStoreChange);
    };
    const getSnapshot = (): Value | undefined => (key === undefined ? undefined : getData(key));
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  }

  function useAllData(): Map<Key, Value> {
    return useSyncExternalStore(subscribeToAll, getAllData, getAllData);
  }

  return {
    getData,
    getAllData,
    setData,
    batchSetData,
    useData,
    useAllData,
    unsetData,
    batchUnsetData,
  };
}
